<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useLayout } from '@/layout/composables/layout';
import { stores } from '@/util/stores.js';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { exportLogs as exportLogsFromDB, clearLogs as clearLogsFromDB } from '@/util/logging/logDB.js';
import { useToast } from '@/composables/useToast.ts';
import EGS from '@/util/egs_util.ts';
import { isReadOnlyImpersonation } from '@/util/apiClient';
import Dialog from '@/components/Dialog/EGS_Dialog.vue';
import EGS_Tooltip from '@/components/Tooltip/Tooltip.vue';
import { useAvailabilityModal } from '@/compostables/useAvailabilityModal';
import AvailabilityCalendarModal from '@/components/Availability/AvailabilityCalendarModal.vue';
import EGS_OverlayBadge from '@/components/OverlayBadge/OverlayBadge.vue';
import { canViewAvailabilityAction } from '@/router/roleAccess';

const toast = useToast();
const { openCalendar } = useAvailabilityModal();

// Availability attention badge (dates needing availability)
const availabilityAttentionCount = computed(() => stores.availabilityStore?.attentionCount || 0);
const profileIncomplete = computed(() => stores.authStore?.showProfileIncompleteNotice || false);
const { onMenuToggle } = useLayout();
const topbarActionClasses =
	'layout-topbar-action inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full !text-[#e4e6e4] transition-colors hover:!bg-[#303633] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e4e6e4]/70';

const allowedRoleNames = ['TESTER', 'DEVELOPER', 'EVERGREEN_SPORTS'];

const canViewRestrictedButtons = computed(() => {
	const userRoles = stores.usersStore.userInfo?.roles || [];
	return userRoles.some((role) => allowedRoleNames.includes(role.name));
});

// Check if sensitive links should be hidden (during read-only impersonation)
const hideSensitiveLinks = computed(() => isReadOnlyImpersonation());
const showProfileIncompleteBadge = computed(() => profileIncomplete.value);
const showAvailabilityAction = computed(() => canViewAvailabilityAction(stores.usersStore.roles));

// Impersonation state
const isImpersonating = computed(() => stores.authStore.isImpersonating);
const impersonatedUser = computed(() => stores.authStore.impersonatedUser);
const impersonationGroupId = computed(() => stores.authStore.impersonationGroupId);
const displayName = computed(() => EGS.util.formatUserWithNumber(impersonatedUser.value));
const isGlobalImpersonation = computed(() => !impersonationGroupId.value);

// Session expired modal state
const showSessionExpiredModal = ref(false);

// Listen for impersonation session expiry events
const handleSessionExpired = () => {
	showSessionExpiredModal.value = true;
};

// Listen for blocked action events and show toast
const handleActionBlocked = () => {
	toast.add({
		severity: 'warn',
		summary: 'Action Blocked',
		detail: 'This action is not available during impersonation. Exit to make changes.',
		life: 4000
	});
};

onMounted(() => {
	window.addEventListener('impersonation-session-expired', handleSessionExpired);
	window.addEventListener('impersonation-action-blocked', handleActionBlocked);
});

onUnmounted(() => {
	window.removeEventListener('impersonation-session-expired', handleSessionExpired);
	window.removeEventListener('impersonation-action-blocked', handleActionBlocked);
});

const exitImpersonation = async () => {
	const result = await stores.authStore.exitImpersonation();
	showSessionExpiredModal.value = false;
	if (result.success) {
		toast.add({
			severity: 'info',
			summary: 'Impersonation Ended',
			detail: 'You are back in your own account.',
			life: 3000
		});
		// Navigate to the appropriate page for the original user
		window.location.href = result.redirectPath || '/app/user';
	} else {
		toast.add({
			severity: 'error',
			summary: 'Could not end impersonation',
			detail: result.message || 'Failed to exit impersonation',
			life: 5000
		});
		// If it fails, force a logout/redirect
		if (result.message?.includes('log in again')) {
			window.location.href = '/login';
		}
	}
};

const exportLogs = async () => {
	try {
		await exportLogsFromDB(); // Call the export function from IndexedDB
		console.log('Logs have been exported successfully');
	} catch (error) {
		console.error('Error exporting logs:', error);
	}
};

const clearLogs = async () => {
	try {
		await clearLogsFromDB(); // Call the clear function from IndexedDB
		console.log('Logs have been cleared successfully');
	} catch (error) {
		console.error('Error clearing logs:', error);
	}
};

const onLogoutClick = () => {
	console.log('logging user out of Evergreen Sports');
	stores.authStore.logout();
};
</script>

<template>
	<div class="layout-topbar sticky top-0 z-[997] flex min-h-16 w-full flex-wrap items-center gap-y-2 !bg-[#15460c] px-2 py-2 !text-[#e4e6e4] dark:!bg-[#15460c] sm:px-8">
		<div class="layout-topbar-logo-container flex max-w-full flex-wrap items-center lg:w-80">
			<button type="button" aria-label="Toggle sidebar" :class="[topbarActionClasses, 'layout-menu-button mr-2']" @click="onMenuToggle">
				<font-awesome-icon icon="bars" />
			</button>

			<!-- Impersonation Indicator - styled like profile incomplete banner -->
			<div v-if="isImpersonating" class="ml-4 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-100 px-3 py-1.5 shadow-sm max-md:ml-2 max-md:px-2">
				<font-awesome-icon icon="user-secret" class="text-[0.85rem] text-amber-800 max-md:text-base" />
				<EGS_Tooltip :text="`Impersonating: ${displayName}`" placement="bottom" class="impersonation-tooltip">
					<span class="max-w-[180px] cursor-default overflow-hidden text-ellipsis whitespace-nowrap text-[0.8rem] font-medium text-amber-800 max-md:hidden">
						{{ displayName }}
					</span>
				</EGS_Tooltip>
				<EGS_Tooltip text="Stop viewing as this user and return to your account" placement="bottom" class="impersonation-tooltip">
					<Button text rounded size="small" class="!h-6 !w-6 !text-amber-800 hover:!bg-amber-900/15 hover:!text-amber-900" @click.stop="exitImpersonation">
						<font-awesome-icon icon="x" />
					</Button>
				</EGS_Tooltip>
			</div>

			<template v-if="canViewRestrictedButtons">
				<button :class="[topbarActionClasses, 'mr-2']" @click="exportLogs">
					<font-awesome-icon :icon="['fas', 'file-arrow-down']" />
				</button>
				<button :class="[topbarActionClasses, 'mr-2']" @click="clearLogs">
					<font-awesome-icon :icon="['fas', 'folder-minus']" />
				</button>
			</template>
		</div>

		<div class="layout-topbar-actions ml-auto max-w-full">
			<div class="layout-topbar-menu">
				<div class="layout-topbar-menu-content flex flex-wrap items-center gap-1 sm:gap-4">
					<EGS_Tooltip v-if="showAvailabilityAction" text="Set the dates and times you are available to officiate" placement="bottom">
						<EGS_OverlayBadge :value="availabilityAttentionCount" :pulse="false" :ringPulse="false">
							<button type="button" :class="topbarActionClasses" @click="openCalendar">
								<font-awesome-icon :icon="['fas', 'calendar-days']" />
								<span class="sr-only">Availability</span>
							</button>
						</EGS_OverlayBadge>
					</EGS_Tooltip>
					<button type="button" :class="topbarActionClasses">
						<font-awesome-icon :icon="['fas', 'inbox']" />
						<span class="sr-only">Messages</span>
					</button>
					<EGS_Tooltip text="View and update your personal profile" placement="bottom">
						<router-link v-if="!hideSensitiveLinks" to="/app/profile">
							<EGS_OverlayBadge v-if="showProfileIncompleteBadge" value="!" :pulse="true" :ringPulse="true">
								<button type="button" :class="topbarActionClasses">
									<font-awesome-icon icon="user" />
									<span class="sr-only">Profile</span>
								</button>
							</EGS_OverlayBadge>
							<button v-else type="button" :class="topbarActionClasses">
								<font-awesome-icon icon="user" />
								<span class="sr-only">Profile</span>
							</button>
						</router-link>
					</EGS_Tooltip>
					<EGS_Tooltip text="Manage your account preferences" placement="bottom">
						<router-link v-if="!hideSensitiveLinks" to="/app/settings">
							<button type="button" :class="topbarActionClasses">
								<font-awesome-icon :icon="['fas', 'gear']" />
								<span class="sr-only">Settings</span>
							</button>
						</router-link>
					</EGS_Tooltip>
					<EGS_Tooltip text="Sign out of your Evergreen Sports account" placement="bottom">
						<router-link to="/login">
							<button type="button" @click="onLogoutClick()" :class="topbarActionClasses">
								<font-awesome-icon :icon="['fas', 'arrow-right-from-bracket']" />
								<span class="sr-only">Logout</span>
							</button>
						</router-link>
					</EGS_Tooltip>
				</div>
			</div>
		</div>
	</div>

	<!-- Availability Calendar Modal -->
	<AvailabilityCalendarModal v-if="showAvailabilityAction" />

	<!-- Session Expired Modal -->
	<Dialog v-model:visible="showSessionExpiredModal" header="Impersonation Session Expired" :modal="true" :closable="false" :draggable="false" class="session-expired-dialog">
		<div class="flex flex-col items-center p-4 text-center">
			<div class="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-amber-100 text-5xl text-amber-800">
				<font-awesome-icon icon="user-clock" />
			</div>
			<p class="m-0 text-base leading-6 text-[var(--text-color)]">Your impersonation session has expired. Click below to return to your account.</p>
		</div>
		<template #footer>
			<Button label="End Impersonation" icon="arrow-left" @click="exitImpersonation" severity="warning" class="w-full" />
		</template>
	</Dialog>
</template>
